import { isEqual } from 'lodash';
import Quagga from 'quagga';
import { Observable, Subject, Subscriber } from 'rxjs';
import { bufferCount, distinctUntilChanged, map } from 'rxjs/operators';

import { BrowserDatamatrixCodeReader, BrowserQRCodeReader } from '@zxing/library'

export const PREFERRED_VIDEO_DEVICE_ID = "preferredVideoDeviceId";
export const VIDEO_DEVICES_IDS = "videoDevicesIds";

export interface Result {
    box: any;
    boxes: any;
    codeResult: {
        code: string;
    };
}

export type Reader = string | { config: Record<'supplements', string[]>; format: string };
/**
 * This class is used to decode barcodes 
 * It uses first the Quagga.js lib to look for 1D barcodes, if no such one is
 * found, a lookup is made with the Zxing library to find 2D barcodes (QRCode and DataMatrix)
 */
export class CameraScanner extends Observable<string> {

    get torch() {
        return this._torch;
    }

    public get hasSeveralDevices() {
        return this.videoDevices && this.videoDevices.length > 1;
    }
    private track: MediaStreamTrack;
    // tslint:disable-next-line:variable-name
    private _torch = false;
    private subscriber: Subscriber<string>;
    private queue$ = new Subject<Result>();
    private readers: Reader[];

    // Size preferences for media device seeking 
    private maxWidth: any = window.innerWidth
    private maxHeight: any = window.innerHeight

    // The index of the preferred device in the list below
    private deviceToUseIndex: number = 0;

    // An array of all available media devices (as returned by enumerateDevices())
    private videoDevices: any[] = [];

    private qrCodeReader: BrowserQRCodeReader
    private dataMatrixReader: BrowserDatamatrixCodeReader

    constructor(readers: Reader[], onStart, onStop, maxWidth?: number, maxHeight?: number) {
        super(o => {
            this.subscriber = o;
        });

        this.onStart = onStart;
        this.onStop = onStop;

        if (maxWidth) {
            this.maxWidth = maxWidth
        }

        if (maxHeight) {
            this.maxHeight = maxHeight
        }

        this.qrCodeReader = new BrowserQRCodeReader()
        this.dataMatrixReader = new BrowserDatamatrixCodeReader()
        this.readers = readers;
        this.queue$
            .pipe(
                bufferCount(2),
                distinctUntilChanged(
                    // we use !isEqual instead of isEqual because we want to emit only if previous and next are equal.
                    // We inverse the operator behavior.
                    (previous: Result[], next: Result[]) => !isEqual(previous, next),
                ),
                map(([result]: Result[]) => result.codeResult.code),
            )
            .subscribe(code => this.subscriber.next(code));
    }

    public async toggleTorch() {
        // Turn torch on
        if (!this._torch) {
            try {
                await this.applyTrackConstraints({
                    advanced: [{ torch: true } as any], // setting it to false to turn off doesn't work
                });
                this._torch = !this._torch;
            } catch (error) {
                // do nothing
            }

        }
        // Turn torch off, for now we are forced to stop the video stream and restart it
        // Maybe there will be a better way to do it in the future, stay tuned !
        else {
            this.stop();
            this.start();
            this._torch = !this._torch;
        }
    }

    public async start() {
        Quagga.onProcessed(this.onProcessed);
        Quagga.onDetected(this.onDetected);
        await this.initDevices();

        const videoDeviceId = localStorage.getItem(PREFERRED_VIDEO_DEVICE_ID);
        return new Promise<void>((resolve, reject) => {
            const constraints = {
                deviceId: videoDeviceId,
                facingMode: 'environment',
                height: this.maxHeight,
                width: (this.maxWidth * 0.8)
            } as MediaTrackConstraints

            Quagga.init(
                {
                    decoder: {
                        readers: this.readers,
                    },
                    frequency: 10,
                    inputStream: {
                        constraints,
                        type: 'LiveStream',
                    },
                    locate: true,
                    locator: {
                        halfSample: false,
                    },
                    numOfWorkers: 2,
                },
                async (error: Error) => {
                    if (error) {
                        reject(error); // logger ?
                    }

                    this.track = Quagga.CameraAccess.getActiveTrack();
                    await Quagga.start();
                    resolve();
                    if (this.onStart !== undefined) {
                        this.onStart()
                    }
                },
            );
        });
    }

    public resetCallbacks() {
        // tslint:disable-next-line:no-empty
        this.onStart = () => { }
        // tslint:disable-next-line:no-empty
        this.onStop = () => { }
    }

    public async stop() {
        Quagga.offProcessed(this.onProcessed);
        Quagga.offDetected(this.onDetected);

        this.onStop();

        return await Quagga.stop();
    }

    public goToNextCameraDevice() {
        this.stop()
        this.deviceToUseIndex = (this.deviceToUseIndex + 1) % this.videoDevices.length;
        this.updatePreferredVideoDeviceIndex();
        this.start();
    }

    // Callbacks to execute after start and before stop
    // tslint:disable-next-line:no-empty
    private onStart = () => { };
    // tslint:disable-next-line:no-empty
    private onStop = () => { };

    /**
     * Updates the id of the preferred device in localStorage
     */
    private updatePreferredVideoDeviceIndex() {
        const preferredDeviceId = this.videoDevices[this.deviceToUseIndex]?.deviceId
        if(preferredDeviceId) {
            localStorage.setItem(PREFERRED_VIDEO_DEVICE_ID, preferredDeviceId)
        }
    }

    private async applyTrackConstraints(constraints: Record<string, any>) {
        if (this.track && typeof this.track.applyConstraints === 'function') {
            await this.track.applyConstraints(constraints);
            return true;
        }

        throw new Error('applyConstraint is not compatible with this platform');
    }

    private onDetected = (result: Result) => this.queue$.next(result);

    private onProcessed = result => {
        // If 1D barcode is found, draw its bounds
        if (result && result.codeResult) {
            this.draw1DBarcodeBounds(result)
        } else {
            this.lookFor2DBarcodes();
        }
    };

    /**
     * Uses the Zxing lib to find QRCode and DataMatrix barcodes
     */
    private lookFor2DBarcodes() {
        const qrCodeReader = new BrowserQRCodeReader()
        const dataMatrixReader = new BrowserDatamatrixCodeReader()

        try {
            const dataUrl = Quagga.canvas.ctx.image.canvas.toDataURL();

            // Trying to decode qrCode
            qrCodeReader.decodeFromImageUrl(dataUrl)
                .then(
                    result => {
                        this.draw2DBarcodeBounds(result)
                        // reshaping result to match quagga data structure
                        this.queue$.next({
                            box: null,
                            boxes: null,
                            codeResult: {
                                code: result.getText()
                            }
                        });
                    }
                )
                .catch(e => {
                    this.clearDrawingContext()
                })

            // Trying to decode datamatrix
            dataMatrixReader.decodeFromImageUrl(dataUrl)
                .then(
                    result => {
                        this.draw2DBarcodeBounds(result)
                        // reshaping result to match quagga data structure
                        this.queue$.next({
                            box: null,
                            boxes: null,
                            codeResult: {
                                code: result.getText()
                            }
                        });
                    }
                )
                .catch(e => {
                    this.clearDrawingContext()
                })

        } catch (e) {
            // tslint:disable-next-line:no-console
            console.error(e)
        }
    }

    /**
     * Uses Quagga.js result to draw barcode's bounding box over screen 
     * @param result The result coming from quagga.js callback onProcessed
     */
    private draw1DBarcodeBounds(result) {
        this.clearDrawingContext()
        const drawingCtx = Quagga.canvas.ctx.overlay;
        if (result.boxes) {
            result.boxes
                .filter(box => {
                    return box !== result.box;
                })
                .forEach(box => {
                    Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
                        color: 'green',
                        lineWidth: 2,
                    });
                });
        }

        if (result.box) {
            Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
                color: '#00F',
                lineWidth: 2,
            });
        }

        if (result.codeResult && result.codeResult.code) {
            Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, drawingCtx, {
                color: 'red',
                lineWidth: 3,
            });
        }
    }

    /**
     * Draws barcode bounds over screen (from Zxing data)
     */
    private draw2DBarcodeBounds(result) {
        const resultPoints = result.resultPoints;
        // 3 result points for a QRCode (3 finder patterns)
        if (resultPoints.length === 3) {
            this.clearDrawingContext()
            resultPoints.map(finderPattern => {
                this.drawFinderPatternMarker(finderPattern)
            })
        }
        // 4 result points for DataMatrix
        else if (resultPoints.length === 4) {
            this.drawDataMatrixBounds(result)
        }

    }

    /**
     * Draws a square over the finder patterns of the QRCode
     * @param finderPattern
     */
    private drawFinderPatternMarker(finderPattern) {
        const drawingCtx = Quagga.canvas.ctx.overlay;
        const finderPatternMarkerSize = 10;

        const finderPatternPath = [
            // top left corner of marker
            [finderPattern.x - finderPatternMarkerSize / 2,
            finderPattern.y - finderPatternMarkerSize / 2],
            // top right corner of marker
            [finderPattern.x + finderPatternMarkerSize / 2,
            finderPattern.y - finderPatternMarkerSize / 2],
            // bottom right corner of marker
            [finderPattern.x + finderPatternMarkerSize / 2,
            finderPattern.y + finderPatternMarkerSize / 2],
            // bottom left corner of marker
            [finderPattern.x - finderPatternMarkerSize / 2,
            finderPattern.y + finderPatternMarkerSize / 2]
        ]

        Quagga.ImageDebug.drawPath(
            finderPatternPath,
            { x: 0, y: 1 },
            drawingCtx, {
            color: 'yellow',
            lineWidth: 10,
        })
    }

    /**
     * Draws a square to materialize DataMatrix Bounds
     * @param result 
     */
    private drawDataMatrixBounds(result) {
        const boundsPoints = result.resultPoints;

        const drawingCtx = Quagga.canvas.ctx.overlay;
        // Drawing QRcode FinderPaterns
        const boundsPath = boundsPoints.map(
            point => [point.x, point.y]
        )

        Quagga.ImageDebug.drawPath(
            boundsPath,
            { x: 0, y: 1 },
            drawingCtx, {
            color: 'blue',
            lineWidth: 10,
        })
    }

    /**
     * Clears all previous drawings to prevent artefacts
     */
    private clearDrawingContext() {
        const drawingCtx = Quagga.canvas.ctx.overlay;
        const drawingCanvas = Quagga.canvas.dom.overlay;
        drawingCtx.clearRect(
            0,
            0,
            parseInt(drawingCanvas.getAttribute('width'), 10),
            parseInt(drawingCanvas.getAttribute('height'), 10),
        );
    }

    private async initDevices() {
        // Fetching preffered video device id from local storage
        const prefferedVideoDeviceId = localStorage.getItem(PREFERRED_VIDEO_DEVICE_ID);
        // Getting available hardware devices
        const devices = await navigator.mediaDevices.enumerateDevices();

        // Filtering devices to eliminate no 'videoinput' devices, and front camera if possible
        // (there is no certain way to eliminate front camera at this point)
        this.videoDevices = devices.filter(device =>
            device.kind === "videoinput" &&
            (!device.label.includes("front") && !device.label.includes("Front"))
        );

        // If ids have changed (typically in Safari), reset preferred device id as it won't match any actual device)
        if (!this.videoDevices.find(device => device.deviceId === prefferedVideoDeviceId)) {
            this.deviceToUseIndex = this.videoDevices.length - 1;
        } else {
            this.deviceToUseIndex = this.videoDevices.map(device => device.deviceId).indexOf(prefferedVideoDeviceId)
        }
        this.updatePreferredVideoDeviceIndex()
    }
}


// import { linkableItemArrivalLocation } from '@skinsoft/entity/src/movement/selectors';
// import { isEqual } from 'lodash';
// import Quagga from 'quagga';
// import { Observable, Subject, Subscriber } from 'rxjs';
// import { bufferCount, distinctUntilChanged, map } from 'rxjs/operators';

// import { BrowserDatamatrixCodeReader, BrowserQRCodeReader } from '@zxing/library'

// export const PREFERRED_VIDEO_DEVICE_ID = "preferredVideoDeviceId";
// export const VIDEO_DEVICES_IDS = "videoDevicesIds";

// export interface Result {
//     box: any;
//     boxes: any;
//     codeResult: {
//         code: string;
//     };
// }

// export type Reader = string | { config: Record<'supplements', string[]>; format: string };
// /**
//  * This class is used to decode barcodes 
//  * It uses first the Quagga.js lib to look for 1D barcodes, if no such one is
//  * found, a lookup is made with the Zxing library to find 2D barcodes (QRCode and DataMatrix)
//  */
// export class CameraScanner extends Observable<string> {

//     get torch() {
//         return this._torch;
//     }

//     public get hasSeveralDevices() {
//         return this.videoDevices && this.videoDevices.length > 1;
//     }
//     private track: MediaStreamTrack;
//     // tslint:disable-next-line:variable-name
//     private _torch = false;
//     private subscriber: Subscriber<string>;
//     private queue$ = new Subject<Result>();
//     private readers: Reader[];

//     // Size preferences for media device seeking 
//     private maxWidth: any = { max: window.innerWidth }
//     private maxHeight: any = { max: window.innerHeight }

//     // The index of the preferred device in the list below
//     private deviceToUseIndex: number = 0;

//     // An array of all available media devices (as returned by enumerateDevices())
//     private videoDevices: any[] = [];

//     private qrCodeReader: BrowserQRCodeReader
//     private dataMatrixReader: BrowserDatamatrixCodeReader

//     constructor(readers: Reader[], onStart, onStop, maxWidth?: number, maxHeight?: number) {
//         super(o => {
//             this.subscriber = o;
//         });

//         this.onStart = onStart;
//         this.onStop = onStop;

//         if (maxWidth) {
//             this.maxWidth = { max: maxWidth }
//         }

//         if (maxHeight) {
//             this.maxHeight = { max: maxHeight }
//         }

//         this.qrCodeReader = new BrowserQRCodeReader()
//         this.dataMatrixReader = new BrowserDatamatrixCodeReader()
//         this.readers = readers;
//         this.queue$
//             .pipe(
//                 bufferCount(2),
//                 distinctUntilChanged(
//                     // we use !isEqual instead of isEqual because we want to emit only if previous and next are equal.
//                     // We inverse the operator behavior.
//                     (previous: Result[], next: Result[]) => !isEqual(previous, next),
//                 ),
//                 map(([result]: Result[]) => result.codeResult.code),
//             )
//             .subscribe(code => this.subscriber.next(code));
//     }

//     public async toggleTorch() {
//         // Turn torch on
//         if (!this._torch) {
//             try {
//                 await this.applyTrackConstraints({
//                     advanced: [{ torch: true } as any], // setting it to false to turn off doesn't work
//                 });
//                 this._torch = !this._torch;
//             } catch (error) {
//                 // do nothing
//             }

//         }
//         // Turn torch off, for now we are forced to stop the video stream and restart it
//         // Maybe there will be a better way to do it in the future, stay tuned !
//         else {
//             this.stop();
//             this.start();
//             this._torch = !this._torch;
//         }
//     }

//     public async start() {
//         Quagga.onProcessed(this.onProcessed);
//         Quagga.onDetected(this.onDetected);

//         // Trying to get device infos from localStorage
//         let videoDeviceId = localStorage.getItem(PREFERRED_VIDEO_DEVICE_ID);
//         this.videoDevices = JSON.parse(localStorage.getItem(VIDEO_DEVICES_IDS) || "null");

//         // If infos are  missing in localStorage, fetching them from MediaDevice API and filling localStorage 
//         // for next usages
//         if (!videoDeviceId || !this.videoDevices) {
//             // filling localStorage
//             await this.initDevices();
//             // Getting infos from localStorage
//             videoDeviceId = localStorage.getItem(PREFERRED_VIDEO_DEVICE_ID);
//             this.videoDevices = JSON.parse(localStorage.getItem(VIDEO_DEVICES_IDS) || "[]");
//         }
//         // Retrieving the index of the preferred device in devices list
//         else {
//             this.deviceToUseIndex = this.videoDevices.map(device => device.deviceId).indexOf(videoDeviceId)
//         }

//         return new Promise((resolve, reject) => {
//             Quagga.init(
//                 {
//                     decoder: {
//                         readers: this.readers,
//                     },
//                     frequency: 10,
//                     inputStream: {
//                         constraints: {
//                             deviceId: videoDeviceId,
//                             facingMode: 'environment',
//                             height: this.maxHeight,
//                             width: this.maxWidth,
//                         },
//                         type: 'LiveStream',
//                     },
//                     locate: true,
//                     locator: {
//                         halfSample: false,
//                     },
//                     numOfWorkers: 2,
//                 },
//                 async (error: Error) => {
//                     if (error) {
//                         reject(error);
//                     }

//                     this.track = Quagga.CameraAccess.getActiveTrack();
//                     await Quagga.start();
//                     resolve();
//                     if (this.onStart !== undefined) {
//                         this.onStart()
//                     }
//                 },
//             );
//         });
//     }

//     public resetCallbacks() {
//         // tslint:disable-next-line:no-empty
//         this.onStart = () => { }
//         // tslint:disable-next-line:no-empty
//         this.onStop = () => { }
//     }

//     public async stop() {
//         Quagga.offProcessed(this.onProcessed);
//         Quagga.offDetected(this.onDetected);

//         this.onStop();

//         return await Quagga.stop();
//     }

//     public goToNextCameraDevice() {
//         this.stop()
//         this.deviceToUseIndex = (this.deviceToUseIndex + 1) % this.videoDevices.length;
//         this.updatePreferredVideoDeviceIndex();
//         this.start();
//     }

//     // Callbacks to execute after start and before stop
//     // tslint:disable-next-line:no-empty
//     private onStart = () => { };
//     // tslint:disable-next-line:no-empty
//     private onStop = () => { };

//     /**
//      * Updates the id of the preferred device in localStorage
//      */
//     private updatePreferredVideoDeviceIndex() {
//         const preferredDeviceId = this.videoDevices[this.deviceToUseIndex].deviceId;
//         localStorage.setItem(PREFERRED_VIDEO_DEVICE_ID, preferredDeviceId)
//     }

//     private async applyTrackConstraints(constraints: Record<string, any>) {
//         if (this.track && typeof this.track.applyConstraints === 'function') {
//             await this.track.applyConstraints(constraints);
//             return true;
//         }

//         throw new Error('applyConstraint is not compatible with this platform');
//     }

//     private onDetected = (result: Result) => this.queue$.next(result);

//     private onProcessed = result => {
//         // If 1D barcode is found, draw its bounds
//         if (result && result.codeResult) {
//             this.draw1DBarcodeBounds(result)
//         } else {
//             this.lookFor2DBarcodes();
//         }
//     };

//     /**
//      * Uses the Zxing lib to find QRCode and DataMatrix barcodes
//      */
//     private lookFor2DBarcodes() {
//         const qrCodeReader = new BrowserQRCodeReader()
//         const dataMatrixReader = new BrowserDatamatrixCodeReader()

//         try {
//             const dataUrl = Quagga.canvas.ctx.image.canvas.toDataURL();

//             // Trying to decode qrCode
//             qrCodeReader.decodeFromImageUrl(dataUrl)
//                 .then(
//                     result => {
//                         this.draw2DBarcodeBounds(result)
//                         // reshaping result to match quagga data structure
//                         this.queue$.next({
//                             box: null,
//                             boxes: null,
//                             codeResult: {
//                                 code: result.getText()
//                             }
//                         });
//                     }
//                 )
//                 .catch(e => {
//                     this.clearDrawingContext()
//                 })

//             // Trying to decode datamatrix
//             dataMatrixReader.decodeFromImageUrl(dataUrl)
//                 .then(
//                     result => {
//                         this.draw2DBarcodeBounds(result)
//                         // reshaping result to match quagga data structure
//                         this.queue$.next({
//                             box: null,
//                             boxes: null,
//                             codeResult: {
//                                 code: result.getText()
//                             }
//                         });
//                     }
//                 )
//                 .catch(e => {
//                     this.clearDrawingContext()
//                 })

//         } catch (e) {
//             // tslint:disable-next-line:no-console
//             console.error(e)
//         }
//     }

//     /**
//      * Uses Quagga.js result to draw barcode's bounding box over screen 
//      * @param result The result coming from quagga.js callback onProcessed
//      */
//     private draw1DBarcodeBounds(result) {
//         this.clearDrawingContext()
//         const drawingCtx = Quagga.canvas.ctx.overlay;
//         if (result.boxes) {
//             result.boxes
//                 .filter(box => {
//                     return box !== result.box;
//                 })
//                 .forEach(box => {
//                     Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
//                         color: 'green',
//                         lineWidth: 2,
//                     });
//                 });
//         }

//         if (result.box) {
//             Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
//                 color: '#00F',
//                 lineWidth: 2,
//             });
//         }

//         if (result.codeResult && result.codeResult.code) {
//             Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, drawingCtx, {
//                 color: 'red',
//                 lineWidth: 3,
//             });
//         }
//     }

//     /**
//      * Draws barcode bounds over screen (from Zxing data)
//      */
//     private draw2DBarcodeBounds(result) {
//         const resultPoints = result.resultPoints;
//         // 3 result points for a QRCode (3 finder patterns)
//         if (resultPoints.length === 3) {
//             this.clearDrawingContext()
//             resultPoints.map(finderPattern => {
//                 this.drawFinderPatternMarker(finderPattern)
//             })
//         }
//         // 4 result points for DataMatrix
//         else if (resultPoints.length === 4) {
//             this.drawDataMatrixBounds(result)
//         }

//     }

//     /**
//      * Draws a square over the finder patterns of the QRCode
//      * @param finderPattern
//      */
//     private drawFinderPatternMarker(finderPattern) {
//         const drawingCtx = Quagga.canvas.ctx.overlay;
//         const finderPatternMarkerSize = 10;

//         const finderPatternPath = [
//             // top left corner of marker
//             [finderPattern.x - finderPatternMarkerSize / 2,
//             finderPattern.y - finderPatternMarkerSize / 2],
//             // top right corner of marker
//             [finderPattern.x + finderPatternMarkerSize / 2,
//             finderPattern.y - finderPatternMarkerSize / 2],
//             // bottom right corner of marker
//             [finderPattern.x + finderPatternMarkerSize / 2,
//             finderPattern.y + finderPatternMarkerSize / 2],
//             // bottom left corner of marker
//             [finderPattern.x - finderPatternMarkerSize / 2,
//             finderPattern.y + finderPatternMarkerSize / 2]
//         ]

//         Quagga.ImageDebug.drawPath(
//             finderPatternPath,
//             { x: 0, y: 1 },
//             drawingCtx, {
//             color: 'yellow',
//             lineWidth: 10,
//         })
//     }

//     /**
//      * Draws a square to materialize DataMatrix Bounds
//      * @param result 
//      */
//     private drawDataMatrixBounds(result) {
//         const boundsPoints = result.resultPoints;

//         const drawingCtx = Quagga.canvas.ctx.overlay;
//         // Drawing QRcode FinderPaterns
//         const boundsPath = boundsPoints.map(
//             point => [point.x, point.y]
//         )

//         Quagga.ImageDebug.drawPath(
//             boundsPath,
//             { x: 0, y: 1 },
//             drawingCtx, {
//             color: 'blue',
//             lineWidth: 10,
//         })
//     }

//     /**
//      * Clears all previous drawings to prevent artefacts
//      */
//     private clearDrawingContext() {
//         const drawingCtx = Quagga.canvas.ctx.overlay;
//         const drawingCanvas = Quagga.canvas.dom.overlay;
//         drawingCtx.clearRect(
//             0,
//             0,
//             parseInt(drawingCanvas.getAttribute('width'), 10),
//             parseInt(drawingCanvas.getAttribute('height'), 10),
//         );
//     }

//     private async initDevices() {
//         // Getting available hardware devices
//         const devices = await navigator.mediaDevices.enumerateDevices();

//         // Filtering devices to eliminate no 'videoinput' devices, and front camera if possible
//         // (there is no certain way to eliminate front camera at this point)
//         this.videoDevices = devices.filter(device =>
//             device.kind === "videoinput" &&
//             (!device.label.includes("front") && !device.label.includes("Front"))
//         );

//         localStorage.setItem(VIDEO_DEVICES_IDS, JSON.stringify(this.videoDevices))
//         this.deviceToUseIndex = this.videoDevices.length - 1;

//         this.updatePreferredVideoDeviceIndex()
//     }
// }
